import { MatrixClient } from 'matrix-js-sdk/src/matrix';

/**
 * Detect whether a returning user needs to unlock secret storage with their security code
 * before `startClient` is called.
 *
 * Uses `getCrossSigningStatus()` to ask the SDK whether the private cross-signing keys are:
 *   (a) already decrypted and cached locally → no action needed, or
 *   (b) only available encrypted on the homeserver → `getSecretStorageKey` will be called
 *       during the initial sync's cross-signing restore, so we must collect the security
 *       code first and pre-cache it.
 *
 * Why NOT `isCrossSigningReady()`: that function returns `true` when
 * `privateKeysInSecretStorage === true` even when `privateKeysCachedLocally` is entirely
 * false — i.e. the keys exist on the server but have not been decrypted yet. Trusting
 * `isCrossSigningReady` caused the probe to false-negative on accounts with server-side
 * cross-signing (the "poisoned account" case), producing
 * "getSecretStorageKey callback returned falsey" mid-sync (see BUG-005, BUG-006).
 * SDK source (rust-crypto.js):
 *   return !!(identity?.isVerified()) && (hasKeysInCache || privateKeysInSecretStorage)
 */
export const checkNeedsSecretStorageUnlock = async (matrixClient: MatrixClient): Promise<boolean> => {
  const crypto = matrixClient.getCrypto();
  if (!crypto) return false;

  const status = await crypto.getCrossSigningStatus();

  // Keys are already decrypted in the local crypto store — no callback needed.
  if (status.privateKeysCachedLocally.masterKey) return false;

  // Private keys must exist encrypted on the server for the unlock callback to fire during sync.
  if (!status.privateKeysInSecretStorage) return false;

  // The unlock gate collects a *security code* (passphrase). An account can have cross-signing
  // metadata in secret storage without the user ever creating a passphrase-backed key — e.g. a
  // user who never finished "Create security code". Opening the gate for them shows a PIN prompt
  // for a code that does not exist (BUG-026). Only gate when a passphrase-protected default key is
  // present; otherwise fall through to startClient → bootstrapCrossSigning, which drives the
  // create-code prompt for genuinely new setups.
  const defaultKeyId = await matrixClient.secretStorage.getDefaultKeyId();
  if (!defaultKeyId) return false;

  const keyTuple = await matrixClient.secretStorage.getKey(defaultKeyId);
  return !!keyTuple?.[1]?.passphrase;
};
